import type { FlowDefinition, FlowFieldDefinition, FlowNode } from '../types';
import { rebuildFlowConnections } from './flows';
import { resolveOrCreateSectionInsertionFlow } from './sectionInsertionFlow';

const BUTTON_CLICK_SOCKET_ID = 'click';

function upsertFields(fields: FlowFieldDefinition[], additions: FlowFieldDefinition[]) {
  const nextById = new Map(fields.map((field) => [field.id, field]));
  additions.forEach((field) => {
    nextById.set(field.id, field);
  });
  return Array.from(nextById.values());
}

function upsertNodes(nodes: FlowNode[], additions: FlowNode[]) {
  const nextByBlockId = new Map(nodes.map((node) => [node.blockId, node]));
  additions.forEach((node) => {
    const existing = nextByBlockId.get(node.blockId);
    if (!existing) {
      nextByBlockId.set(node.blockId, node);
      return;
    }

    const mergeBindings = (
      current: FlowNode['inputs'] | FlowNode['outputs'],
      defaults: FlowNode['inputs'] | FlowNode['outputs']
    ) => {
      const next = [...current];
      defaults.forEach((binding) => {
        if (!next.some((entry) => entry.socketId === binding.socketId)) {
          next.push(binding);
        }
      });
      return next;
    };

    nextByBlockId.set(node.blockId, {
      ...existing,
      id: existing.id || node.id,
      blockType: node.blockType,
      config: { ...(node.config ?? {}), ...(existing.config ?? {}) },
      inputs: mergeBindings(existing.inputs, node.inputs),
      outputs: mergeBindings(existing.outputs, node.outputs),
    });
  });
  return Array.from(nextByBlockId.values());
}

function normalizeButtonLabel(label: string | null | undefined) {
  const trimmed = typeof label === 'string' ? label.trim() : '';
  return trimmed.length > 0 ? trimmed : 'Button';
}

export function getButtonClickFieldId(blockId: string) {
  return `button-click-${blockId}`;
}

export function getButtonClickFieldLabel(label: string | null | undefined) {
  return `${normalizeButtonLabel(label)} Click`;
}

function buildButtonClickField(blockId: string, label: string | null | undefined): FlowFieldDefinition {
  return {
    id: getButtonClickFieldId(blockId),
    label: getButtonClickFieldLabel(label),
    type: 'event.trigger',
  };
}

function buildButtonFlowNode(blockId: string): FlowNode {
  return {
    id: `flow-node-${blockId}`,
    blockId,
    blockType: 'button',
    inputs: [],
    outputs: [{ socketId: BUTTON_CLICK_SOCKET_ID, fieldId: getButtonClickFieldId(blockId) }],
  };
}

export function upsertButtonFlowNode(
  flows: FlowDefinition[],
  args: {
    sectionId: string;
    blockId: string;
    label?: string | null;
    sections?: Array<{
      id: string;
      settings?: {
        canvas?: {
          blocks?: Array<{
            id: string;
            type: string;
          }>;
        };
      };
    }> | null;
  }
) {
  const { flows: nextFlows, flow: targetFlow } = resolveOrCreateSectionInsertionFlow(flows, {
    sectionId: args.sectionId,
    sections: args.sections,
  });

  if (!targetFlow) return nextFlows;

  const updatedFlowBase: FlowDefinition = {
    ...targetFlow,
    fields: upsertFields(targetFlow.fields, [buildButtonClickField(args.blockId, args.label)]),
    nodes: upsertNodes(targetFlow.nodes, [buildButtonFlowNode(args.blockId)]),
  };

  const updatedFlow: FlowDefinition = {
    ...updatedFlowBase,
    connections: rebuildFlowConnections(updatedFlowBase),
  };

  return nextFlows.map((flow) => (flow.id === updatedFlow.id ? updatedFlow : flow));
}

export function upsertButtonFlowNodesForSections(
  flows: FlowDefinition[],
  sections: Array<{
    id: string;
    settings?: {
      canvas?: {
        blocks?: Array<{
          id: string;
          type: string;
          content?: Record<string, unknown>;
        }>;
      };
    };
  }>
) {
  return sections.reduce((nextFlows, section) => {
    const buttons = section.settings?.canvas?.blocks?.filter((block) => block.type === 'button');
    if (!buttons || buttons.length === 0) return nextFlows;
    return buttons.reduce(
      (currentFlows, block) =>
        upsertButtonFlowNode(currentFlows, {
          sectionId: section.id,
          blockId: block.id,
          label: typeof block.content?.label === 'string' ? block.content.label : 'Button',
          sections,
        }),
      nextFlows
    );
  }, flows);
}
